import React, { useEffect, useRef } from "react";
import { StatusResponse } from "../../../services/instantNewsletterService";
import instantNewsletterService from "../../../services/instantNewsletterService";

interface BuildingStepProps {
  jobId: string;
  onComplete: (result: { magicLink: string; editionId: number }) => void;
  onError: (error: string) => void;
}

const STATUS_MESSAGES: Record<string, string> = {
  pending: "Queued...",
  building_blueprint: "Setting up your newsletter template...",
  building_edition: "Creating your sample edition...",
  writing_content: "Writing sample content with AI...",
  generating_images: "Generating images for your newsletter...",
  sending_preview: "Sending preview to your inbox...",
  completed: "Done!",
};

const BuildingStep: React.FC<BuildingStepProps> = ({ jobId, onComplete, onError }) => {
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const [status, setStatus] = React.useState<StatusResponse | null>(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const result = await instantNewsletterService.pollStatus(jobId);
        setStatus(result);

        if (result.status === "completed" && result.magic_link && result.edition_id) {
          if (pollRef.current) clearInterval(pollRef.current);
          onComplete({ magicLink: result.magic_link, editionId: result.edition_id });
        } else if (result.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          onError(result.error || "Build failed. Please try again.");
        }
      } catch {
        // Network error — keep polling
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, onComplete, onError]);

  const message = status ? (STATUS_MESSAGES[status.status] || status.message || "Working...") : "Starting...";

  return (
    <div style={{ textAlign: "center", maxWidth: "400px", width: "100%" }}>
      <div style={{ marginBottom: "2rem" }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #e0e0e0",
            borderTopColor: "#000",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 1.5rem",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
      <h2 style={{ fontSize: "1.3rem", marginBottom: "0.5rem" }}>
        Building your newsletter
      </h2>
      <p style={{ color: "#666", fontSize: "0.95rem" }}>
        {message}
      </p>
      <p style={{ color: "#999", fontSize: "0.8rem", marginTop: "1.5rem" }}>
        This usually takes about 3-4 minutes.
      </p>
    </div>
  );
};

export default BuildingStep;
